import { ApiState, KAryTreeNode, ServerResponseN } from "../../../types/baseType";
import { AsObjectHashMap, foldTreeNodes } from "../../../context/map/utilMutableStore";
import { GeoZoneHashElement, ZoneTypes, ZoneTypesValues } from "../../../context/map/fields/fieldsStore";
import { createAsyncThunk, createEntityAdapter, createSlice, EntityId, prepareAutoBatched } from "@reduxjs/toolkit";
import { applyExtraReducer } from "../weather";
import { PayloadAction } from "@reduxjs/toolkit";
import { WsChange } from "@api/socket/WebSocketService";
import { match, P } from "ts-pattern";
import { array, string } from "fp-ts";
import logger from "@utils/logger";
import cultivation from "./wsHandlers/cultivation";

const httpClient = require('@infobis/api-module').default;

type GeoZoneNode = KAryTreeNode<GeoZoneHashElement>
type GeoZonesByType = {
    [k in ZoneTypesValues]: string[]
};

interface GeoZoneState extends ApiState {
    apiError: undefined,
    isApiLoading: false,
    geoZoneChangedIds: string[],
    geoZonesByType: GeoZonesByType
}

const fetch = createAsyncThunk(
    'geoZoneTree',
    async (_, thunkAPI) => {
        try {
            const response = await httpClient.makeRequest({
                url: `/fieldTree?_ds=1638266027802&loadDate=2021-11-24T08:15:26.732Z`,
                params: {
                    method: 'GET',
                },
                authorize: true
            }) as ServerResponseN<GeoZoneNode>
            if (response) {
                return response
            } else {
                throw new Error('Invalid response ' + response)
            }
        } catch (e: any) {
            const message = `An error occurred while fetching API ` + (e?.message || e || '')
            return thunkAPI.rejectWithValue({ message })
        }
    }
)

export type WsChangePayload = { changes: WsChange[] }

const log = logger.getLogger("geoZones.store")

export const geoZoneAdapter = createEntityAdapter<GeoZoneHashElement, EntityId>({ selectId: (e) => e.glid })
export const geoZoneSelectors = geoZoneAdapter.getSelectors()
const geoZonesByType = () =>
    Object.values(ZoneTypes).reduce((acc, x) => ({ ...acc, [x]: [] }), {}) as GeoZonesByType

const slice = createSlice({
    name: 'geoZoneTreeSlice',
    initialState: geoZoneAdapter.getInitialState<GeoZoneState>({
        isApiLoading: false,
        apiError: undefined,
        geoZonesByType: geoZonesByType(),
        geoZoneChangedIds: []
    }),
    reducers: {
        commit(state: GeoZoneState, action: PayloadAction<{ changes: string[] }>) {
            state.geoZoneChangedIds = array.difference(string.Eq)(state.geoZoneChangedIds, action.payload.changes)
        },
        applyWsChange: {
            prepare: prepareAutoBatched<WsChangePayload>(),
            reducer(state, actions: PayloadAction<WsChangePayload>) {
                log.debug("Apply changes, count - ", actions.payload.changes.length)
                state.geoZoneChangedIds.push(...array.uniq(string.Eq)(array.reduce<WsChange, string[]>([],
                    (acc: string[], change: WsChange) => {
                        let geoZone = state.entities[change.id]
                        if (geoZone)
                            cultivation(geoZone)(match(change.values))
                                .with(P._, () => geoZone!.dynamic = { ...change.values }).exhaustive()
                        return array.append(String(change.id))(acc)
                    })(actions.payload.changes)))
            }
        }
    },
    extraReducers: (builder) => {
        applyExtraReducer(builder, fetch, {
            fulfillReducer: (state, action) => {
                const data: {
                    entities: AsObjectHashMap<GeoZoneHashElement>,
                    geoZonesByType: GeoZonesByType
                } = {
                    entities: {},
                    geoZonesByType: geoZonesByType()
                }
                const {
                    entities,
                    geoZonesByType: gzByType
                } = foldTreeNodes<GeoZoneHashElement, typeof data>(data)(action.payload,
                    (acc, node, parentNode) => {
                        node.zoneType = node.zoneType || 'other'
                        node.groupName = (parentNode?.groupName || []).concat(node.glid)

                        acc.entities[node.glid] = node
                        acc.geoZonesByType[node.zoneType] = acc.geoZonesByType[node.zoneType].concat(node.glid)

                        return [acc, node]
                    })
                geoZoneAdapter.removeAll(state)
                geoZoneAdapter.setMany(state, entities)
                state.geoZonesByType = gzByType
            }
        })
    },
})

export const actions = { ...slice.actions, fetch }
export default slice.reducer